/**
 * Client-side pagination helpers (offset/limit style, matches club hunt log).
 */

export const DEFAULT_PAGE_SIZE = 20;

/**
 * @param {{ count: number, page?: number, pageSize?: number }} params
 * @returns {{
 *   offset: number,
 *   limit: number,
 *   page: number,
 *   totalPages: number,
 *   count: number,
 *   startIndex: number,
 *   endIndex: number,
 *   hasPagination: boolean,
 * }}
 */
export function getPaginationMeta({ count, page = 0, pageSize = DEFAULT_PAGE_SIZE }) {
  const safeCount = Math.max(0, Number(count) || 0);
  const limit = Math.max(1, Number(pageSize) || DEFAULT_PAGE_SIZE);
  const totalPages = safeCount === 0 ? 1 : Math.ceil(safeCount / limit);
  const safePage = Math.min(Math.max(0, Number(page) || 0), totalPages - 1);
  const offset = safePage * limit;
  const startIndex = safeCount === 0 ? 0 : offset + 1;
  const endIndex = Math.min(offset + limit, safeCount);

  return {
    offset,
    limit,
    page: safePage,
    totalPages,
    count: safeCount,
    startIndex,
    endIndex,
    hasPagination: safeCount > limit,
  };
}

/**
 * @template T
 * @param {T[]} items
 * @param {{ page?: number, pageSize?: number }} options
 * @returns {{ items: T[], meta: ReturnType<typeof getPaginationMeta> }}
 */
export function paginateItems(items, { page = 0, pageSize = DEFAULT_PAGE_SIZE } = {}) {
  const list = Array.isArray(items) ? items : [];
  const meta = getPaginationMeta({ count: list.length, page, pageSize });
  const slice = list.slice(meta.offset, meta.offset + meta.limit);
  return { items: slice, meta };
}
